import {DestroyRef, Injector, Signal, inject} from '@angular/core';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {StoragePort} from '@platform/storage/domain/ports/storage.port';
import {firstValueFrom} from 'rxjs';
import {debounceTime, filter, switchMap, skip} from 'rxjs/operators';

export interface SynchronizableStore<T> {
  state: Signal<T>;

  patch(val: T): void;
}

export interface SynchronizationOptions<T> {
  debounce?: number;
  when?: (state: T) => boolean;
}

export class StorageSynchronizer<T> {
  private readonly injector = inject(Injector);

  constructor(private readonly storage: StoragePort<T>, private readonly store: SynchronizableStore<T>) 
  {}

  sync(options: SynchronizationOptions<T> = {}): () => Promise<void> {
    const {debounce = 100, when = (): boolean => true} = options;

    return async () => {
      await this.hydrate();

      toObservable(this.store.state, {injector: this.injector})
        .pipe(
          skip(1),
          filter(when),
          debounceTime(debounce),
          switchMap((state) => this.storage.set(state)),
          takeUntilDestroyed(this.injector.get(DestroyRef))
        )
        .subscribe();
    };
  }

  private async hydrate(): Promise<void> {
    const saved = await firstValueFrom(this.storage.get());

    if (saved) {
      this.store.patch(saved);
    }
  }
}
